import type { RouterContextType } from "@/api/permissions/permissions.api";
import AuthenticatedLayout from "@/components/features/AuthenticatedLayout/AuthenticatedLayout";
import { SessionExpiredBoundary } from "@/components/features/AuthenticatedLayout/SessionExpiredBoundary";
import { initLocationWatchIfPermitted, stopLocationWatch } from "@/lib/geoLocation";
import { useServiceProviderOnboardingStore } from "@/store/useServiceProviderOnboardingStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useEffect } from "react";

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
  component: AuthenticatedLayoutWrapper,
});

export function AuthenticatedLayoutWrapper() {
  const location = useLocation();
  const { serviceProviderId, clearServiceProviderId } = useServiceProviderOnboardingStore();

  // Clear serviceProviderId when navigating away from onboarding routes
  useEffect(() => {
    const isOnboardingRoute = location.pathname.startsWith("/service-providers/onboarding");

    if (!isOnboardingRoute && serviceProviderId) {
      // User navigated away from onboarding routes, clear serviceProviderId
      clearServiceProviderId();
    }
  }, [location.pathname, serviceProviderId, clearServiceProviderId]);

  // Starts the location watch silently once the user is authenticated, but only
  // when the browser already granted geolocation permission (Permissions API) —
  // it never prompts on load. The cleanup below stops it when this layout
  // unmounts (leaving the authenticated route tree); clearAllStores() also calls
  // stopLocationWatch() directly on logout/401, since that redirect can lag
  // behind the unmount.
  useEffect(() => {
    void initLocationWatchIfPermitted();
    return () => stopLocationWatch();
  }, []);

  if (location.pathname.startsWith("/mobile")) {
    return <Outlet />;
  }

  return (
    <SessionExpiredBoundary>
      <AuthenticatedLayout>
        <Outlet />
      </AuthenticatedLayout>
    </SessionExpiredBoundary>
  );
}
